import React, { useEffect, useState } from "react";
import { backend_url } from "../App";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaCheckCircle, FaBoxOpen, FaShippingFast, FaClipboardCheck,
  FaUser, FaMapMarkerAlt, FaPhone, FaEnvelope, FaVenusMars,
  FaCity, FaGlobe, FaEdit, FaSave, FaSignOutAlt, FaHistory,
  FaShoppingBag,
} from "react-icons/fa";
import { MdDashboard, MdLocationOn } from "react-icons/md";
import { toast } from "react-toastify";

const indianStates = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa",
  "Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala",
  "Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland",
  "Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura",
  "Uttar Pradesh","Uttarakhand","West Bengal",
];

const statusIcons = {
  Confirmed: <FaClipboardCheck className="text-blue-500" />,
  Packed: <FaBoxOpen className="text-yellow-500" />,
  Shipped: <FaShippingFast className="text-orange-500" />,
  Delivered: <FaCheckCircle className="text-green-500" />,
};

const UserDetails = () => {
  const location = useLocation();
  const [user, setUser] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const queryParams = new URLSearchParams(location.search);
  const [activeTab, setActiveTab] = useState(queryParams.get("tab") || "profile");
  const navigate = useNavigate();

  // Single fetch — reads userId from API response directly (no stale closure)
  useEffect(() => {
    const fetchAll = async () => {
      const token = localStorage.getItem("auth-token");
      if (!token) { navigate("/login"); return; }
      try {
        const res = await fetch(`${backend_url}/getuserdetails`, {
          headers: { "auth-token": token },
        });
        const data = await res.json();
        if (data.success) setUser(data.user);
      } catch (err) {
        console.error("Error fetching user details:", err);
      }
    };
    fetchAll();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("auth-token");
    navigate("/login");
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/user-profile?tab=${tab}`, { replace: true });
  };

  // Profile completion
  const profileFields = ["name","email","phoneNumber","gender","address","city","pincode","country"];
  const filled = profileFields.filter((f) => user[f] && user[f] !== "").length;
  const completion = Math.round((filled / profileFields.length) * 100);

  // Initials avatar
  const initials = (user.name || "U").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  const sidebarItems = [
    { id: "profile", label: "Profile Details", icon: <FaUser /> },
    { id: "orders", label: "Order History", icon: <FaHistory /> },
    { id: "myOrders", label: "My Orders", icon: <FaShoppingBag /> },
  ];

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(180deg,#F6F1FE 0%,#f9fafb 100%)" }}>
      <div className="max-w-7xl mx-auto px-4 py-8 flex flex-col md:flex-row gap-6">

        {/* ── Sidebar ── */}
        <aside className="w-full md:w-72 flex-shrink-0">
          {/* User card */}
          <div className="rounded-2xl overflow-hidden shadow-md mb-4" style={{ background: "linear-gradient(135deg,#3A2060,#8353C6)" }}>
            <div className="px-6 pt-6 pb-4 flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-black text-white shadow-lg mb-3"
                style={{ background: "linear-gradient(135deg,#9E79D2,#6939AC)" }}>
                {initials}
              </div>
              <p className="text-white font-bold text-lg leading-tight">{user.name || "User"}</p>
              <p className="text-purple-200 text-xs mt-0.5 truncate max-w-full">{user.email || ""}</p>
              {/* Completion bar */}
              <div className="w-full mt-4">
                <div className="flex justify-between text-[11px] text-purple-200 mb-1">
                  <span>Profile completion</span>
                  <span className="font-bold">{completion}%</span>
                </div>
                <div className="w-full h-1.5 bg-white/20 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${completion}%`, background: "linear-gradient(90deg,#9E79D2,#6939AC)" }} />
                </div>
              </div>
            </div>
          </div>

          {/* Nav items */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            {sidebarItems.map((item) => (
              <button key={item.id} onClick={() => handleTabChange(item.id)}
                className={`w-full flex items-center gap-3 px-5 py-3.5 text-sm font-semibold transition-all duration-200 border-b border-gray-50 last:border-b-0
                  ${activeTab === item.id
                    ? "text-white"
                    : "text-gray-600 hover:text-purple-700 hover:bg-purple-50"}`}
                style={activeTab === item.id
                  ? { background: "linear-gradient(90deg,#8353C6,#9E79D2)" }
                  : {}}
              >
                <span className={activeTab === item.id ? "text-white" : "text-purple-400"}>{item.icon}</span>
                {item.label}
              </button>
            ))}
            <button onClick={handleLogout}
              className="w-full flex items-center gap-3 px-5 py-3.5 text-sm font-semibold text-red-500 hover:bg-red-50 transition-all duration-200">
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </aside>

        {/* ── Main ── */}
        <main className="flex-1 min-w-0">
          {activeTab === "profile" && (
            <ProfileSection user={user} setUser={setUser} editMode={editMode}
              setEditMode={setEditMode} saving={saving} setSaving={setSaving}
              errors={errors} setErrors={setErrors} completion={completion} />
          )}
          {activeTab === "orders" && <TrackOrderSection />}
          {activeTab === "myOrders" && <OrdersTable user={user} backend_url={backend_url} />}
        </main>
      </div>
    </div>
  );
};

// ──────────────────────── PROFILE SECTION ────────────────────────
const ProfileSection = ({ user, setUser, editMode, setEditMode, saving, setSaving, errors, setErrors, completion }) => {
  const validatePhone = (v) => {
    if (!v) return "Phone number is required.";
    if (!/^\d+$/.test(v)) return "Only numbers allowed.";
    if (v.length !== 10) return "Must be 10 digits.";
    return "";
  };

  const handleSave = async () => {
    const phoneErr = validatePhone(user.phoneNumber);
    if (phoneErr) { setErrors({ phoneNumber: phoneErr }); return; }
    try {
      setSaving(true);
      const token = localStorage.getItem("auth-token");
      const res = await fetch(`${backend_url}/saveuserdetails`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "auth-token": token },
        body: JSON.stringify(user),
      });
      const data = await res.json();
      if (data.success) {
        setEditMode(false);
        toast.success("Profile saved!");
      }
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  const fieldDefs = [
    { label: "Full Name", key: "name", icon: <FaUser />, readonly: true },
    { label: "Email", key: "email", icon: <FaEnvelope />, readonly: true },
    { label: "Phone Number", key: "phoneNumber", icon: <FaPhone />, type: "tel",
      onChange: (e) => {
        const v = e.target.value.replace(/\D/g, "");
        if (v.length <= 10) { setUser({ ...user, phoneNumber: v }); setErrors({ phoneNumber: validatePhone(v) }); }
      }
    },
    { label: "Gender", key: "gender", icon: <FaVenusMars />, isSelect: true,
      options: ["Male", "Female", "Other", "Prefer not to say"] },
    { label: "Address", key: "address", icon: <FaMapMarkerAlt /> },
    { label: "City", key: "city", icon: <FaCity /> },
    { label: "Pincode", key: "pincode", icon: <MdLocationOn />,
      onChange: (e) => setUser({ ...user, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) }) },
    { label: "Country", key: "country", icon: <FaGlobe />, isSelect: true,
      options: ["India", "Other"],
      onChange: (e) => setUser({ ...user, country: e.target.value, state: "" }) },
  ];

  if (user.country === "India") {
    fieldDefs.push({ label: "State", key: "state", icon: <FaMapMarkerAlt />,
      isSelect: true, options: indianStates });
  }

  return (
    <div className="space-y-4">
      {/* Incomplete banner */}
      {completion < 100 && !editMode && (
        <div onClick={() => setEditMode(true)}
          className="flex items-center gap-3 p-4 rounded-2xl border border-yellow-200 cursor-pointer hover:bg-yellow-50 transition"
          style={{ background: "#fffbeb" }}>
          <span className="text-2xl">✏️</span>
          <div className="flex-1">
            <p className="text-sm font-bold text-yellow-800">Complete your profile — {completion}% done</p>
            <p className="text-xs text-yellow-600 mt-0.5">Fill in your details to auto-fill orders faster. Click to edit.</p>
          </div>
          <div className="text-yellow-500">
            <FaEdit />
          </div>
        </div>
      )}

      {/* Profile card */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-purple-50"
          style={{ background: "linear-gradient(135deg,#F6F1FE,#F6F1FE)" }}>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center"
              style={{ background: "linear-gradient(135deg,#8353C6,#9E79D2)" }}>
              <FaUser className="text-white text-xs" />
            </div>
            <h2 className="text-base font-bold text-gray-800">Profile Details</h2>
          </div>
          {editMode ? (
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-xl text-white shadow transition hover:opacity-90"
              style={{ background: "linear-gradient(135deg,#059669,#10b981)" }}>
              <FaSave size={12} />{saving ? "Saving…" : "Save Changes"}
            </button>
          ) : (
            <button onClick={() => setEditMode(true)}
              className="flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-xl text-white shadow transition hover:opacity-90"
              style={{ background: "linear-gradient(135deg,#8353C6,#9E79D2)" }}>
              <FaEdit size={12} /> Edit
            </button>
          )}
        </div>

        {/* Fields grid */}
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {fieldDefs.map(({ label, key, icon, readonly, type = "text", isSelect, options, onChange }) => {
            const val = user[key];
            const isEmpty = !val || val === "";
            return (
              <div key={key} className={`rounded-xl p-3.5 border transition-all ${
                isEmpty && !editMode ? "border-orange-200 bg-orange-50/40" : "border-gray-100 bg-gray-50"}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-purple-400 text-xs">{icon}</span>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">{label}</label>
                  {isEmpty && !editMode && <span className="ml-auto text-[10px] text-orange-500 font-semibold bg-orange-100 px-2 py-0.5 rounded-full">Empty</span>}
                </div>
                {editMode && !readonly ? (
                  isSelect ? (
                    <select value={val || ""} onChange={onChange || ((e) => setUser({ ...user, [key]: e.target.value }))}
                      className="w-full text-sm bg-white border border-purple-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-400 outline-none">
                      <option value="">Select {label}</option>
                      {options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input type={type} value={val || ""}
                      onChange={onChange || ((e) => setUser({ ...user, [key]: e.target.value }))}
                      className={`w-full text-sm bg-white border rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-400 outline-none ${errors[key] ? "border-red-400" : "border-purple-200"}`} />
                  )
                ) : (
                  <p className={`text-sm font-semibold ${isEmpty ? "text-orange-400 italic" : "text-gray-800"}`}>
                    {isEmpty ? "Not set" : val}
                  </p>
                )}
                {errors[key] && <p className="text-red-500 text-xs mt-1">{errors[key]}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ──────────────────────── TRACK ORDER ────────────────────────
const TrackOrderSection = () => {
  const [orderId, setOrderId] = useState("");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleTrackOrder = async () => {
    if (!orderId.trim()) { setError("Please enter a valid Order ID"); return; }
    setLoading(true); setOrder(null); setError("");
    try {
      const res = await fetch(`${backend_url}/track-order/${orderId}`);
      const data = await res.json();
      if (data.success) setOrder(data.order);
      else setError("Order not found.");
    } catch { setError("Something went wrong."); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6">
      <div className="flex items-center gap-2 mb-6">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg,#8353C6,#9E79D2)" }}>
          <FaShippingFast className="text-white text-xs" />
        </div>
        <h2 className="text-base font-bold text-gray-800">Track Your Order</h2>
      </div>

      <div className="flex gap-3 mb-4">
        <input type="text" value={orderId} onChange={(e) => setOrderId(e.target.value)}
          placeholder="Enter Order ID"
          className="flex-1 border border-purple-200 p-3 rounded-xl text-sm focus:ring-2 focus:ring-purple-400 outline-none bg-purple-50/30" />
        <button onClick={handleTrackOrder}
          className="text-white px-6 py-3 rounded-xl text-sm font-bold shadow hover:opacity-90 transition"
          style={{ background: "linear-gradient(135deg,#8353C6,#9E79D2)" }}>
          {loading ? "Tracking…" : "Track"}
        </button>
      </div>

      {error && <p className="text-red-500 text-sm font-medium">{error}</p>}

      {order && (
        <div className="mt-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            {[["Order ID", order._id],["Customer", order.name],["Amount", `₹${order.totalPrice}`],["Date", new Date(order.date).toLocaleDateString("en-IN")]].map(([k,v]) => (
              <div key={k} className="bg-purple-50 rounded-xl p-3 border border-purple-100">
                <p className="text-xs text-purple-500 font-bold uppercase mb-0.5">{k}</p>
                <p className="text-gray-800 font-semibold text-sm break-all">{v}</p>
              </div>
            ))}
          </div>

          {/* Timeline */}
          <div className="overflow-x-auto py-4">
            <div className="flex justify-between items-center gap-2 min-w-[500px] relative">
              {["Confirmed","Packed","Shipped","Delivered"].map((step, idx) => {
                const steps = ["Confirmed","Packed","Shipped","Delivered"];
                const currentIndex = steps.indexOf(order.status);
                const isCompleted = idx < currentIndex;
                const isActive = idx === currentIndex;
                return (
                  <div key={step} className="flex flex-col items-center w-1/4 relative">
                    {idx < 3 && (
                      <div className="absolute top-6 left-1/2 h-1 w-full bg-gray-200 -z-0">
                        <div className={`h-1 transition-all duration-700 ${isCompleted ? "bg-purple-400" : ""}`} style={{ width: isCompleted ? "100%" : isActive ? "50%" : "0%" }} />
                      </div>
                    )}
                    <div className={`w-12 h-12 flex items-center justify-center rounded-full border-4 text-xl z-10 transition-all duration-500
                      ${isCompleted ? "border-purple-400 bg-purple-100 text-purple-600"
                        : isActive ? "border-purple-500 bg-purple-50 text-purple-600 animate-pulse"
                        : "border-gray-200 bg-gray-50 text-gray-400"}`}>
                      {statusIcons[step]}
                    </div>
                    <span className={`mt-2 text-xs font-semibold ${isCompleted ? "text-purple-600" : isActive ? "text-purple-700" : "text-gray-400"}`}>{step}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ──────────────────────── ORDERS TABLE ────────────────────────
const OrdersTable = ({ user, backend_url }) => {
  const [myOrders, setMyOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 5;
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelOrderId, setCancelOrderId] = useState(null);

  useEffect(() => {
    if (!user?.userId) return;
    const fetchOrders = async () => {
      try {
        const res = await fetch(`${backend_url}/orders/user/${user.userId}`);
        const data = await res.json();
        if (data.success) setMyOrders(data.orders || []);
      } catch { console.error("Error fetching orders"); }
    };
    fetchOrders();
  }, [backend_url, user?.userId]);

  const getDeliveryDate = (d) => {
    if (!d) return "N/A";
    const date = new Date(d);
    date.setDate(date.getDate() + 5);
    return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const statusColor = (s) => ({
    Pending: "bg-yellow-100 text-yellow-700",
    Confirmed: "bg-blue-100 text-blue-700",
    Packed: "bg-purple-100 text-purple-700",
    Shipped: "bg-indigo-100 text-indigo-700",
    "Out for Delivery": "bg-orange-100 text-orange-700",
    Delivered: "bg-green-100 text-green-700",
    Cancelled: "bg-red-100 text-red-700",
  }[s] || "bg-gray-100 text-gray-700");

  const indexOfLastOrder = currentPage * ordersPerPage;
  const currentOrders = myOrders.slice(indexOfLastOrder - ordersPerPage, indexOfLastOrder);
  const totalPages = Math.ceil(myOrders.length / ordersPerPage);

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-6 py-4 border-b border-purple-50" style={{ background: "linear-gradient(135deg,#F6F1FE,#F6F1FE)" }}>
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg,#8353C6,#9E79D2)" }}>
          <FaShoppingBag className="text-white text-xs" />
        </div>
        <h2 className="text-base font-bold text-gray-800">My Orders</h2>
        <span className="ml-auto text-xs font-bold px-2.5 py-1 rounded-full text-white" style={{ background: "linear-gradient(90deg,#8353C6,#9E79D2)" }}>
          {myOrders.length} orders
        </span>
      </div>

      {myOrders.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <FaShoppingBag className="text-5xl mx-auto mb-3 text-purple-200" />
          <p className="font-semibold">No orders yet</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr style={{ background: "linear-gradient(90deg,#F6F1FE,#F6F1FE)" }}>
                  {["Order ID","Product","Price","Qty","Est. Delivery","Status"].map((h) => (
                    <th key={h} className="py-3 px-4 text-left text-xs font-bold text-purple-700 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentOrders.map((order, idx) => (
                  <tr key={order._id} className={`border-b border-gray-50 hover:bg-purple-50/30 transition ${idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}>
                    <td className="py-3 px-4 font-mono text-xs text-gray-500">{order._id.slice(-7).toUpperCase()}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <img crossOrigin="anonymous"
                          src={order.productId?.images?.[0] || ""}
                          alt={order.productId?.name || "Product"}
                          className="w-12 h-12 object-cover rounded-lg border border-purple-100 flex-shrink-0" />
                        <span className="text-gray-800 font-medium line-clamp-2 max-w-[160px]">{order.productId?.name || "Unknown"}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 font-bold text-purple-700">₹{order.pricePerUnit}</td>
                    <td className="py-3 px-4 text-gray-600">{order.quantity}</td>
                    <td className="py-3 px-4 text-gray-500 text-xs">{getDeliveryDate(order.orderDate)}</td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col gap-1 items-start">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${statusColor(order.status)}`}>{order.status}</span>
                        {order.status !== "Cancelled" && order.status !== "Delivered" && (
                          <button onClick={() => { setCancelOrderId(order._id); setShowCancelModal(true); }}
                            className="text-red-400 text-xs hover:text-red-600 font-semibold">Cancel</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-center items-center gap-2 py-4 border-t border-gray-50">
            <button onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 hover:bg-purple-100 disabled:opacity-40 transition">Prev</button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button key={i} onClick={() => setCurrentPage(i + 1)}
                className={`w-8 h-8 rounded-lg text-xs font-bold transition ${currentPage === i + 1 ? "text-white shadow" : "bg-gray-100 hover:bg-purple-50 text-gray-600"}`}
                style={currentPage === i + 1 ? { background: "linear-gradient(135deg,#8353C6,#9E79D2)" } : {}}>{i + 1}</button>
            ))}
            <button onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 hover:bg-purple-100 disabled:opacity-40 transition">Next</button>
          </div>
        </>
      )}

      {/* Cancel modal */}
      {showCancelModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 px-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="px-6 py-4 flex items-center justify-between" style={{ background: "linear-gradient(135deg,#8353C6,#9E79D2)" }}>
              <h2 className="text-white font-bold text-lg">Cancel Order?</h2>
              <button onClick={() => setShowCancelModal(false)} className="text-white text-2xl leading-none hover:opacity-70">×</button>
            </div>
            <div className="p-6 space-y-2 text-sm text-gray-700">
              {[["💰 Refunds","Processed to original payment within 5–15 business days"],
                ["⏰ Time limit","Request must be raised within 7 days of delivery"],
                ["🧵 Fabric condition","Must be unused, unwashed, and in original packaging"],
                ["⚠️ Exclusions","Custom-printed and tailored orders are non-returnable"]].map(([k,v]) => (
                <div key={k} className="flex gap-2 p-3 bg-gray-50 rounded-xl">
                  <span className="font-bold text-gray-800 whitespace-nowrap">{k}:</span>
                  <span className="text-gray-600">{v}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
              <button onClick={() => setShowCancelModal(false)}
                className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 transition">Go Back</button>
              <button onClick={async () => {
                  try {
                    const res = await fetch(`${backend_url}/edit-order/${cancelOrderId}`, {
                      method: "PUT", headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ status: "Cancelled" }),
                    });
                    const data = await res.json();
                    if (data.success) {
                      toast.success("Order cancelled.");
                      setMyOrders((p) => p.map((o) => o._id === cancelOrderId ? { ...o, status: "Cancelled" } : o));
                    } else toast.error(data.message || "Failed.");
                  } catch { toast.error("Something went wrong."); }
                  finally { setShowCancelModal(false); setCancelOrderId(null); }
                }}
                className="flex-1 py-2.5 rounded-xl text-white text-sm font-bold hover:opacity-90 transition"
                style={{ background: "linear-gradient(135deg,#ef4444,#dc2626)" }}>
                Confirm Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetails;
